import React, { useState, useEffect, useCallback } from 'react';
import { getClearances, createClearance, updateClearance, deleteClearance } from '../../services/api';

const CLEARANCE_LEVELS = ['S', 'TS', 'TS/SCI with poly'];
const STATUSES = ['Submission Preparation', 'Submitted', 'Approved', 'Denied'];
const P_STAR_OPTIONS = ['N/A', 'Yes', 'No'];

const STATUS_COLORS = {
  'Submission Preparation': 'var(--status-pending)',
  Submitted: 'var(--status-active)',
  Approved: 'var(--status-completed)',
  Denied: 'var(--status-critical)',
};

const EMPTY_FORM = {
  employee_name: '',
  clearance_level: '',
  status: 'Submission Preparation',
  date_submitted: '',
  last_status_check: '',
  sponsoring_program: '',
  sponsoring_agency: '',
  p_star_completed: 'N/A',
  p_star_date: '',
  investigation_completed: '',
  approval_date: '',
  notes: '',
};

export default function ClearanceTracking({ onNavigate }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState('All');

  // Sorting
  const [sortKey, setSortKey] = useState('employee_name');
  const [sortDir, setSortDir] = useState('asc');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClearances();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load clearance records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // --- Filtering & Sorting ---
  const filtered = records.filter((r) => {
    if (statusFilter !== 'All' && r.status !== statusFilter) return false;
    if (levelFilter !== 'All' && r.clearance_level !== levelFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (r.employee_name || '').toLowerCase().includes(q) ||
        (r.sponsoring_program || '').toLowerCase().includes(q) ||
        (r.sponsoring_agency || '').toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey] || '';
    const bv = b[sortKey] || '';
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  // --- Modal ---
  const openCreate = () => {
    setEditingRecord(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setApiError('');
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setForm({
      employee_name: record.employee_name || '',
      clearance_level: record.clearance_level || '',
      status: record.status || 'Submission Preparation',
      date_submitted: record.date_submitted || '',
      last_status_check: record.last_status_check || '',
      sponsoring_program: record.sponsoring_program || '',
      sponsoring_agency: record.sponsoring_agency || '',
      p_star_completed: record.p_star_completed || 'N/A',
      p_star_date: record.p_star_date || '',
      investigation_completed: record.investigation_completed || '',
      approval_date: record.approval_date || '',
      notes: record.notes || '',
    });
    setFormErrors({});
    setApiError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.employee_name.trim()) errs.employee_name = 'Required';
    if (!form.clearance_level) errs.clearance_level = 'Required';
    if (!form.status) errs.status = 'Required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (editingRecord) {
        // Build delta — only send changed fields
        const delta = {};
        Object.keys(form).forEach((key) => {
          if (form[key] !== (editingRecord[key] || '')) {
            delta[key] = form[key];
          }
        });
        if (Object.keys(delta).length === 0) {
          closeModal();
          return;
        }
        await updateClearance(editingRecord.clearance_id, delta);
      } else {
        await createClearance(form);
      }
      closeModal();
      await fetchRecords();
    } catch (err) {
      setApiError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingRecord) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete the clearance record for "${editingRecord.employee_name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      await deleteClearance(editingRecord.clearance_id);
      closeModal();
      await fetchRecords();
    } catch (err) {
      setApiError(err.message || 'Failed to delete record');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Summary ---
  const summary = {
    total: records.length,
    prep: records.filter((r) => r.status === 'Submission Preparation').length,
    submitted: records.filter((r) => r.status === 'Submitted').length,
    approved: records.filter((r) => r.status === 'Approved').length,
    denied: records.filter((r) => r.status === 'Denied').length,
  };

  // --- Render ---
  if (loading) {
    return (
      <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
        Loading clearance records...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'var(--status-critical)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
        {error}
        <br />
        <button className="btn btn-outline" style={{ marginTop: '16px' }} onClick={fetchRecords}>Retry</button>
      </div>
    );
  }

  return (
    <div className="contracts-page" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
      {/* Summary Bar */}
      <div className="contracts-summary">
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Total</span>
          <span className="contracts-summary-value">{summary.total}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Preparation</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-pending)' }}>{summary.prep}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Submitted</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-active)' }}>{summary.submitted}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Approved</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-completed)' }}>{summary.approved}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Denied</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-critical)' }}>{summary.denied}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Search by name, program, agency, or notes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 240px', minWidth: '200px' }}
        />
        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="All">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-select" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="All">All Levels</option>
          {CLEARANCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <button className="btn btn-primary" onClick={openCreate}>
          + New Record
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('employee_name')}>Name{sortIndicator('employee_name')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('clearance_level')}>Level{sortIndicator('clearance_level')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('sponsoring_agency')}>Agency{sortIndicator('sponsoring_agency')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('sponsoring_program')}>Program{sortIndicator('sponsoring_program')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date_submitted')}>Submitted{sortIndicator('date_submitted')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('p_star_completed')}>P*{sortIndicator('p_star_completed')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('investigation_completed')}>Inv.{sortIndicator('investigation_completed')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', color: 'var(--bcore-text-dim)', padding: '40px' }}>
                  {records.length === 0 ? 'No clearance records yet. Click "+ New Record" to add one.' : 'No records match your filters.'}
                </td>
              </tr>
            ) : sorted.map((r) => (
              <React.Fragment key={r.clearance_id}>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === r.clearance_id ? null : r.clearance_id)}
                >
                  <td style={{ fontWeight: 600 }}>{r.employee_name}</td>
                  <td>{r.clearance_level}</td>
                  <td>
                    <span className="badge-status" style={{
                      background: STATUS_COLORS[r.status] || 'var(--bcore-text-dim)',
                      color: '#000',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.sponsoring_agency || '\u2014'}</td>
                  <td>{r.sponsoring_program || '\u2014'}</td>
                  <td>{r.date_submitted || '\u2014'}</td>
                  <td>{r.p_star_completed || '\u2014'}</td>
                  <td>{r.investigation_completed || '\u2014'}</td>
                  <td>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
                {expandedId === r.clearance_id && (
                  <tr className="expanded-row">
                    <td colSpan={9}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', padding: '12px 8px' }}>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Last Status Check</div>
                          <div>{r.last_status_check || '\u2014'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>P* Date</div>
                          <div>{r.p_star_date || '\u2014'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Approval Date</div>
                          <div>{r.approval_date || '\u2014'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Created By</div>
                          <div>{r.createdBy || '\u2014'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Last Updated</div>
                          <div>{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '\u2014'}</div>
                        </div>
                        {r.notes && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Notes</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--bcore-text-dim)' }}>
        Showing {sorted.length} of {records.length} record{records.length !== 1 ? 's' : ''}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        }} onClick={closeModal}>
          <div
            style={{
              background: 'var(--bcore-bg-secondary, #242424)',
              borderRadius: '12px',
              padding: '32px',
              width: '100%',
              maxWidth: '640px',
              maxHeight: '85vh',
              overflowY: 'auto',
              border: '1px solid var(--bcore-border, #333)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontFamily: 'Kode Mono, monospace',
              fontSize: '18px',
              textTransform: 'uppercase',
              marginBottom: '24px',
              color: 'var(--bcore-text-primary)',
            }}>
              {editingRecord ? 'Edit Clearance Record' : 'New Clearance Record'}
            </h2>

            {apiError && (
              <div style={{ background: 'rgba(248,113,113,0.15)', border: '1px solid var(--status-critical)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: 'var(--status-critical)', fontSize: '13px' }}>
                {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Row 1: Name + Level */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Employee Name <span className="required">*</span></label>
                  <input className="form-input" value={form.employee_name} onChange={(e) => handleChange('employee_name', e.target.value)} />
                  {formErrors.employee_name && <div className="form-error">{formErrors.employee_name}</div>}
                </div>
                <div>
                  <label className="form-label">Clearance Level <span className="required">*</span></label>
                  <select className="form-select" value={form.clearance_level} onChange={(e) => handleChange('clearance_level', e.target.value)}>
                    <option value="">Select...</option>
                    {CLEARANCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {formErrors.clearance_level && <div className="form-error">{formErrors.clearance_level}</div>}
                </div>
              </div>

              {/* Row 2: Status + Date Submitted */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Status <span className="required">*</span></label>
                  <select className="form-select" value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {formErrors.status && <div className="form-error">{formErrors.status}</div>}
                </div>
                <div>
                  <label className="form-label">Date Submitted</label>
                  <input className="form-input" type="date" value={form.date_submitted} onChange={(e) => handleChange('date_submitted', e.target.value)} />
                </div>
              </div>

              {/* Row 3: Agency + Program */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Sponsoring Agency</label>
                  <input className="form-input" value={form.sponsoring_agency} onChange={(e) => handleChange('sponsoring_agency', e.target.value)} placeholder="e.g., Honda, Porsche, Lexus" />
                </div>
                <div>
                  <label className="form-label">Sponsoring Program</label>
                  <input className="form-input" value={form.sponsoring_program} onChange={(e) => handleChange('sponsoring_program', e.target.value)} />
                </div>
              </div>

              {/* Row 4: P* Completed + P* Date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">P* Completed</label>
                  <select className="form-select" value={form.p_star_completed} onChange={(e) => handleChange('p_star_completed', e.target.value)}>
                    {P_STAR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">P* Date</label>
                  <input className="form-input" type="date" value={form.p_star_date} onChange={(e) => handleChange('p_star_date', e.target.value)} />
                </div>
              </div>

              {/* Row 5: Investigation + Approval Date + Last Status Check */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Investigation Completed</label>
                  <select className="form-select" value={form.investigation_completed} onChange={(e) => handleChange('investigation_completed', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Approval Date</label>
                  <input className="form-input" type="date" value={form.approval_date} onChange={(e) => handleChange('approval_date', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Last Status Check</label>
                  <input className="form-input" type="date" value={form.last_status_check} onChange={(e) => handleChange('last_status_check', e.target.value)} />
                </div>
              </div>

              {/* Row 6: Notes */}
              <div style={{ marginBottom: '24px' }}>
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {editingRecord ? (
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ color: 'var(--status-critical)', borderColor: 'var(--status-critical)' }}
                    onClick={handleDelete}
                    disabled={submitting}
                  >
                    Delete Record
                  </button>
                ) : <div />}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" className="btn btn-outline" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (editingRecord ? 'Saving...' : 'Creating...') : (editingRecord ? 'Save Changes' : 'Create Record')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
